import { useEffect, useMemo, useState } from 'react';

interface UseCountdownProps {
  initialTime: number;
  resetTrigger: boolean;
  // ! onTick must be a React callback to prevent infinite rerenders
  onTick?: (timeLeft: number) => void;
}

export const useCountdown = ({ initialTime, resetTrigger, onTick }: UseCountdownProps) => {
  const [timeLeft, setTimeLeft] = useState<number>(initialTime);

  useMemo(() => {
    setTimeLeft(initialTime);
    onTick?.(initialTime);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- resetTrigger is needed to allow the
    useMemo to watch if it changes, other deps are exhaustive */
  }, [initialTime, onTick, resetTrigger]);

  useEffect(() => {
    const timerId = setInterval(() => {
      // Note - the state updater function is called twice by React in development mode
      // This doesn't happen in prod, don't drive yourself crazy trying to stop it :)
      setTimeLeft((currentTimeLeft) => {
        if (currentTimeLeft <= 0) {
          clearInterval(timerId);
          onTick?.(0);
          return 0;
        }

        const newTimeLeft = currentTimeLeft - 1;
        onTick?.(newTimeLeft);
        return newTimeLeft;
      });
    }, 1000);

    return () => {
      clearInterval(timerId);
    };
  }, [onTick, resetTrigger]);

  return timeLeft;
};
